import React, { useState, useEffect, useRef } from 'react'
import { useFetch, usePost } from '../../../components/component-items/helpers'
import { globalState } from '../../../store'

import { Form, InputGroup, Col, Row } from 'react-bootstrap'
import { useDropzone } from 'react-dropzone'
import styled from 'styled-components'

// Components
import GlobalStyles from '../../../components/component-items/styles'
import Checkbox from '../../../components/component-items/checkbox'
import { HoverNotes } from '../../../components/component-items/hover-notes'
import { CenteredSpinner } from '../../../components/component-items/loading-popover'
import { DragNDrop } from './styles'
import { CustomDateInput } from '../../../components/component-items/custom-date-input'
import { Range } from 'react-date-range'

import { AcceptProps } from './types'
import { TransportMode, TransportModeOptions } from '../../../types/transport'

const SectionHeader = styled.h5`
  margin-top: 1.5em;
  margin-bottom: 1em;
  padding-bottom: 0.5em;
  border-bottom: 1px solid #e0e0e0;
  color: #666;
  font-size: 0.9em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const FieldHelp = styled.span`
  display: inline-block;

  > div:before {
    width: 260px;
    white-space: normal;
  }
`

function Accept({ file, setFile, fileTypes }: AcceptProps) {
  const { acceptedFiles, fileRejections, getRootProps, getInputProps } = useDropzone({
    onDrop: (files) => setFile(files[0]),
    // @ts-ignore
    accept: { '': '.csv' },
  })

  const AcceptedFilesItems = acceptedFiles.map((file: any) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ))

  const RejectedFilesItems = fileRejections.map((file: any) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ))

  return (
    <DragNDrop>
      <div {...getRootProps({ className: 'dropzone' })}>
        <input {...getInputProps()} />
        <div style={{ textAlign: 'center' }}>
          <p>Drag &apos;n&apos; drop or click to select files</p>(<em>Only {fileTypes} file types</em>)
        </div>
      </div>
      <aside>
        {file && fileRejections.length ? (
          <>
            <h4>Rejected files</h4>
            <ul>{RejectedFilesItems}</ul>
          </>
        ) : null}
        {file && AcceptedFilesItems.length ? (
          <>
            <h4>Accepted files</h4>
            <ul>{AcceptedFilesItems}</ul>
          </>
        ) : null}
      </aside>
    </DragNDrop>
  )
}

export const CreateOrder = () => {
  const {
    state: { csrf, companyId, defaultWarehouse },
  } = globalState()

  const [loading, setLoading] = useState(false)
  const [validated, setValidated] = useState(false)

  const [addressName, setAddressName] = useState('')
  const [addressEmail, setAddressEmail] = useState('')
  const [originatorNumber, setOriginatorNumber] = useState('')
  const [originatorId, setOriginatorId] = useState<string | null>(null)
  const [company, setCompany] = useState('')
  const [addressLine1, setAddressLine1] = useState('')
  const [addressLine2, setAddressLine2] = useState('')
  const [addressCity, setAddressCity] = useState('')
  const [addressState, setAddressState] = useState('')
  const [addressZip, setAddressZip] = useState('')
  const [addressCountry, setAddressCountry] = useState('US')
  const [addressPhone, setAddressPhone] = useState('')
  const [warehouse, setWarehouse] = useState(defaultWarehouse.id)

  const [orderNote, setOrderNote] = useState('')
  const [wholesalePricing, setWholesalePricing] = useState(false)
  const [transportMode, setTransportMode] = useState<TransportMode>('parcel')
  const [b2bOrder, setB2bOrder] = useState(false)
  const [shipBetweenDates, setShipBetweenDates] = useState<any>('before')

  const {
    response: warehouse_resp,
    error,
    loaded,
    placeholder,
  }: any = useFetch(`/api/company/${companyId}/warehouse_list/`, {})
  const warehouses = loaded ? warehouse_resp.warehouses : []

  const counries_resp: any = useFetch('/api/address/countries/', {})
  const countries = counries_resp.loaded ? counries_resp.response.countries : []

  const node = useRef<HTMLFormElement | null>(null)

  useEffect(() => {
    if (loaded && warehouse_resp) {
      setWarehouse(warehouse_resp.warehouseId)
    }
  }, [loaded])

  const handleSubmit = async (event: any) => {
    event.preventDefault()
    event.stopPropagation()

    if (loading) {
      return
    }

    setValidated(true)

    if (node?.current?.checkValidity() === false) {
      return
    }

    setLoading(true)
    await usePost(
      '/api/order/',
      {
        shipping_name: addressName,
        email: addressEmail,
        warehouse: warehouse,
        shipping_address_1: addressLine1,
        shipping_address_2: addressLine2,
        shipping_city: addressCity,
        shipping_state: addressState,
        shipping_zip: addressZip,
        shipping_country: addressCountry,
        shipping_phone: addressPhone,
        originator_number: originatorNumber || null,
        originator_id: originatorId || null,
        shipping_company: company,
        note: orderNote,
        use_wholesale_prices: wholesalePricing,
        transport_mode: transportMode,
        b_to_b: b2bOrder,
        ...(['range', 'before'].includes(shipBetweenDates)
          ? {
              ship_before: date ? `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}` : '',
            }
          : {}),
        ...(['range', 'after'].includes(shipBetweenDates)
          ? {
              ship_after: afterDate
                ? `${afterDate.getFullYear()}-${afterDate.getMonth() + 1}-${afterDate.getDate()}`
                : '',
            }
          : {}),
      },
      csrf,
      false
    )
    setLoading(false)
  }

  var currentDate = new Date()
  currentDate.setDate(currentDate.getDate() + 1)
  const [date, setDate] = useState<Date>(currentDate)
  const [afterDate, setAfterDate] = useState<Date | null>(null)
  const [rangeDate, setRangeDate] = useState<Range>({
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection',
  })

  const handleDateChange = (newDate: Date | Range | null) => {
    if (newDate instanceof Date) {
      if (shipBetweenDates === 'after') {
        setAfterDate(newDate)
      } else {
        setDate(newDate)
      }
    } else if (newDate && 'startDate' in newDate && 'endDate' in newDate) {
      const startDate = newDate.startDate
      const endDate = newDate.endDate

      if (startDate && endDate) {
        setRangeDate({
          startDate,
          endDate,
          key: 'selection',
        })
        setAfterDate(startDate)
        setDate(endDate)
      }
    }
  }

  return (
    <Form noValidate ref={node} validated={validated} onSubmit={handleSubmit}>
      <GlobalStyles.FullPageCard>
        <GlobalStyles.CardHeader>
          <GlobalStyles.CardTitle>
            <h3>Create Order</h3>
          </GlobalStyles.CardTitle>
        </GlobalStyles.CardHeader>
        <div style={{ margin: '2em auto', width: '65%' }}>
          <SectionHeader style={{ marginTop: 0 }}>Shipping Address</SectionHeader>
          <Row>
            <Form.Group as={Col} md="6" className="mb-3 required">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                type="text"
                placeholder="Full Name"
                name="name"
                value={addressName}
                maxLength={100}
                onChange={(e) => setAddressName(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">Please enter the customers full name.</Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="6" className="required mb-3">
              <Form.Label>Email</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Email"
                  name="email"
                  aria-describedby="inputGroupPrepend"
                  required
                  maxLength={100}
                  value={addressEmail}
                  onChange={(e) => setAddressEmail(e.target.value)}
                />
                <InputGroup.Text id="inputGroupPrepend">@</InputGroup.Text>
                <Form.Control.Feedback type="invalid">Please add an email.</Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} md="6" className="required mb-3">
              <Form.Label>Address 1</Form.Label>
              <Form.Control
                type="text"
                placeholder="Address 1"
                maxLength={100}
                name="line1"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                required
              />
              <Form.Control.Feedback type="invalid">Please provide a valid Address.</Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="3" className="mb-3">
              <Form.Label>Address 2</Form.Label>
              <Form.Control
                type="text"
                placeholder="Address 2"
                maxLength={100}
                name="line2"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
              />
            </Form.Group>
            <Form.Group as={Col} md="3" className="mb-3">
              <Form.Label>Company</Form.Label>
              <Form.Control
                type="text"
                name="business"
                placeholder="Company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} md="6" className="required mb-3">
              <Form.Label>City</Form.Label>
              <Form.Control
                type="text"
                placeholder="City"
                name="city"
                maxLength={100}
                value={addressCity}
                onChange={(e) => setAddressCity(e.target.value)}
                required
              />
              <Form.Control.Feedback type="invalid">Please provide a valid city.</Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="3" className="required mb-3">
              <Form.Label>State</Form.Label>
              <Form.Control
                type="text"
                placeholder="State"
                name="state"
                maxLength={40}
                value={addressState}
                onChange={(e) => setAddressState(e.target.value)}
                required
              />
              <Form.Control.Feedback type="invalid">Please provide a valid state.</Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="3" className="required mb-3">
              <Form.Label>Zip</Form.Label>
              <Form.Control
                type="text"
                placeholder="Zip"
                name="zip"
                maxLength={10}
                value={addressZip}
                onChange={(e) => setAddressZip(e.target.value)}
                required
              />
              <Form.Control.Feedback type="invalid">
                Please provide a valid zip. If there is none, enter 000000
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} md="6" className="required mb-3">
              <Form.Label>Country</Form.Label>
              <Form.Select name="country" value={addressCountry} onChange={(e) => setAddressCountry(e.target.value)}>
                {Object.keys(countries).map((c, id) => (
                  <option value={c} key={id}>
                    {countries[c]}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">Please provide a valid country.</Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="6" className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="text"
                placeholder="Phone"
                name="tel"
                maxLength={100}
                value={addressPhone}
                onChange={(e) => setAddressPhone(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">Please provide a valid phone number.</Form.Control.Feedback>
            </Form.Group>
          </Row>
          <SectionHeader>Shipping & Fulfillment</SectionHeader>
          <Row>
            <Form.Group as={Col} md="6" className="required mb-3">
              <Form.Label>Warehouse</Form.Label>
              <Form.Select name="warehouse" value={warehouse} onChange={(e: any) => setWarehouse(e.target.value)}>
                {warehouses?.map((w: any, id: number) => (
                  <option value={w.id} key={id}>
                    {w.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md="6" className="mb-3" controlId="new-order-originator-number">
              <Form.Label>
                <FieldHelp>
                  <HoverNotes
                    title="Originator Number"
                    description="External order or PO number. Does not need to be unique."
                  />
                </FieldHelp>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Originator Number"
                value={originatorNumber}
                onChange={(e) => setOriginatorNumber(e.target.value)}
              />
            </Form.Group>
            <Form.Group as={Col} md="6" className="mb-3" controlId="new-order-originator-id">
              <Form.Label>
                <FieldHelp>
                  <HoverNotes
                    title="Originator ID"
                    description="Source identifier used to match orders. Must be unique within the company."
                  />
                </FieldHelp>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Originator ID"
                value={originatorId || ''}
                onChange={(e) => setOriginatorId(e.target.value)}
              />
            </Form.Group>
            <Form.Group as={Col} md="6" className="mb-3">
              <Form.Label>Order Type</Form.Label>
              <Form.Select
                name="b2bOrder"
                value={b2bOrder ? 'b2b' : 'dtc'}
                onChange={(e) => setB2bOrder(e.target.value === 'b2b')}
              >
                <option value="dtc">DTC (Direct to Consumer)</option>
                <option value="b2b">B2B / Wholesale</option>
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md="6" className="mb-3">
              <Form.Label>Transport Mode</Form.Label>
              <Form.Select
                name="transportMode"
                value={transportMode}
                onChange={(e) => setTransportMode(e.target.value as TransportMode)}
              >
                {TransportModeOptions.map((option) => (
                  <option value={option.value} key={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>
          {b2bOrder ? (
            <Row>
              <Form.Group as={Col} md="6" className="mb-3">
                <Form.Label>Ship Date Type</Form.Label>
                <Form.Select
                  name="shipDateType"
                  value={shipBetweenDates}
                  onChange={(e) => setShipBetweenDates(e.target.value)}
                >
                  <option value="before">Ship Before</option>
                  <option value="after">Ship After</option>
                  <option value="range">Ship Between</option>
                </Form.Select>
              </Form.Group>
              <Form.Group as={Col} md="6" className="mb-3">
                <Form.Label>Ship Date</Form.Label>
                <CustomDateInput
                  value={shipBetweenDates === 'range' ? rangeDate : shipBetweenDates === 'after' ? afterDate : date}
                  onChange={handleDateChange}
                  mode={shipBetweenDates === 'range' ? 'range' : 'single'}
                  placeholder={shipBetweenDates === 'range' ? 'MM-DD-YYYY to MM-DD-YYYY' : 'MM-DD-YYYY'}
                  required
                />
              </Form.Group>
            </Row>
          ) : null}
          <Row>
            <Form.Group as={Col} md="12" className="form-group" controlId="exampleForm.ControlTextarea1">
              <Form.Label>Order Notes (Shown on Packing Slip)</Form.Label>
              <Form.Control
                as="textarea"
                maxLength={1000}
                rows={3}
                value={orderNote}
                onChange={(e) => setOrderNote(e.target.value)}
              />
            </Form.Group>
          </Row>
          <InputGroup>
            <Checkbox selected={wholesalePricing} setSelected={() => setWholesalePricing(!wholesalePricing)} />
            <p style={{ padding: '0 1em', lineHeight: '24px' }}>Use Wholesale Pricing</p>
          </InputGroup>

          {!loading ? (
            <GlobalStyles.Button onClick={(e) => handleSubmit(e)} style={{ minWidth: 150, margin: 0, float: 'right' }}>
              Next
            </GlobalStyles.Button>
          ) : (
            <GlobalStyles.Button style={{ minWidth: 150, margin: 0, float: 'right', cursor: 'auto' }}>
              <CenteredSpinner />
              &nbsp;
            </GlobalStyles.Button>
          )}
        </div>
      </GlobalStyles.FullPageCard>
    </Form>
  )
}
